// Authoritative catalogue mirror of core-entities AllPermissions (BEZ-5876+).
// Used when the caller has access_all_sellers and may grant any catalogue permission.
// Omit unused / dead codes that should not appear in the role editor (e.g. view_order_summary).
export const ALL_PERMISSION_CODES = [
	"bulk_operation_cancel_orders",
	"bulk_operation_hide_skus",
	"edit_courier_configuration",
	"create_order",
	"import_orders",
	"view_inventory_detail",
	"edit_order",
	"edit_order_copy_split_cancel",
	"create_return_label",
	"view_proof_of_delivery",
	"view_home_page",
	"view_inventory_summary",
	"view_asn_summary",
	"view_returns_summary",
	"view_invoices",
	"view_reports",
	"view_invoices_internal",
	"create_invoice",
	"upload_invoice",
	"view_settings",
	"import_products",
	"view_debug_page",
	"view_help_centre",
	"raise_a_ticket",
	"view_raise_a_ticket",
	"approve_asn_delivery_slot_request",
	"reject_asn_delivery_slot_request",
	"view_order_claims",
	"view_order",
	"release_pack_and_hold_order",
	"force_order_sync",
	"force_asn_sync",
	"force_product_sync",
	"force_stock_level_sync",
	"force_return_sync",
	"view_audit_logs",
	"create_product",
	"access_frozen_seller",
	"view_extra_order_details",
	"view_extra_asn_details",
	"update_product_status",
	"view_bulk_actions",
	"view_all_products_detail",
	"create_user_seller_role",
	"create_order_claim",
	"raise_claim",
	"create_stock_transfer_request",
	"edit_order_comment",
	"access_all_sellers",
	"manage_seller_roles",
];

/** Short plain-English meaning for each catalogue permission (role UI + guide). */
export const PERMISSION_DESCRIPTIONS = {
	access_all_sellers: "Access every seller (Bezos staff only)",
	access_frozen_seller: "Open frozen sellers (Bezos staff only)",
	approve_asn_delivery_slot_request: "Approve a requested ASN delivery slot",
	bulk_operation_cancel_orders: "Cancel many orders at once from the orders list",
	bulk_operation_hide_skus: "Hide many products at once from the products list",
	create_invoice: "Create invoices",
	create_order: "Create orders manually in the portal",
	create_order_claim: "Create an order claim",
	create_product: "Create products in the portal",
	create_return_label: "Create a return shipping label for an order",
	create_stock_transfer_request: "Create a stock transfer request",
	create_user_seller_role: "Create, remove, and change roles for seller users",
	edit_courier_configuration: "Edit courier / shipping configuration (Bezos staff only)",
	edit_order: "Edit order details (address, lines, etc.)",
	edit_order_comment: "Add or edit comments on an order",
	edit_order_copy_split_cancel: "Copy, split, or cancel an order",
	force_asn_sync: "Force-sync an ASN with the warehouse (Bezos staff only)",
	force_order_sync: "Force-sync an order with the warehouse (Bezos staff only)",
	force_product_sync: "Force-sync a product with the warehouse (Bezos staff only)",
	force_return_sync: "Force-sync a return with the warehouse (Bezos staff only)",
	force_stock_level_sync: "Force-sync stock levels with the warehouse (Bezos staff only)",
	import_orders: "Import orders from a file",
	import_products: "Import products from a file",
	manage_seller_roles: "Create and edit custom roles and their permissions",
	raise_a_ticket: "Submit a support ticket",
	raise_claim: "Raise a claim from the claims flow",
	reject_asn_delivery_slot_request: "Reject a requested ASN delivery slot",
	release_pack_and_hold_order: "Release a pack-and-hold order",
	update_product_status: "Change product status without raising a ticket",
	upload_invoice: "Upload invoice files",
	view_all_products_detail: "View full product detail, including restricted fields",
	view_asn_summary: "Open goods-in / ASN pages",
	view_audit_logs: "Open the audit logs page",
	view_bulk_actions: "Show bulk-action controls on list pages",
	view_debug_page: "Open debug tooling (Bezos staff only)",
	view_extra_asn_details: "Show extra ASN fields normally hidden from sellers",
	view_extra_order_details: "Show extra order fields (e.g. internal detail)",
	view_help_centre: "Open the help centre",
	view_home_page: "Open the portal home / summary page",
	view_inventory_detail: "Open full inventory / stock detail for a product",
	view_inventory_summary: "Open the inventory / stock list",
	view_invoices: "Open seller invoices",
	view_invoices_internal: "Open internal Bezos invoice screens (Bezos staff only)",
	view_order: "Open orders, order detail, and late deliveries",
	view_order_claims: "Open the order claims list",
	view_proof_of_delivery: "View proof of delivery on an order",
	view_raise_a_ticket: "Open the raise-a-ticket page",
	view_reports: "Open and download reports",
	view_returns_summary: "Open the returns list",
	view_settings: "Open Settings",
};

export const CREATE_USER_PERMISSION = "create_user_seller_role";
export const MANAGE_ROLES_PERMISSION = "manage_seller_roles";
export const ACCESS_ALL_SELLERS_PERMISSION = "access_all_sellers";

/** Display order for role UI. Unknown codes fall into Other. */
export const PERMISSION_CATEGORY_ORDER = [
	"Portal",
	"Orders",
	"Inventory",
	"Goods in",
	"Returns",
	"Claims",
	"Invoices",
	"Reports",
	"Audit",
	"Courier",
	"Users and roles",
	"Staff",
	"Other",
];

const OTHER_PERMISSION_CATEGORY = "Other";

/** Product-area grouping for the role editor, guide, and roles table. */
export const PERMISSION_CATEGORIES = {
	view_home_page: "Portal",
	view_help_centre: "Portal",
	raise_a_ticket: "Portal",
	view_raise_a_ticket: "Portal",
	view_settings: "Portal",
	view_bulk_actions: "Portal",
	view_order: "Orders",
	create_order: "Orders",
	import_orders: "Orders",
	edit_order: "Orders",
	edit_order_copy_split_cancel: "Orders",
	edit_order_comment: "Orders",
	bulk_operation_cancel_orders: "Orders",
	view_proof_of_delivery: "Orders",
	release_pack_and_hold_order: "Orders",
	view_extra_order_details: "Orders",
	force_order_sync: "Orders",
	view_inventory_summary: "Inventory",
	view_inventory_detail: "Inventory",
	view_all_products_detail: "Inventory",
	create_product: "Inventory",
	import_products: "Inventory",
	update_product_status: "Inventory",
	bulk_operation_hide_skus: "Inventory",
	create_stock_transfer_request: "Inventory",
	force_product_sync: "Inventory",
	force_stock_level_sync: "Inventory",
	view_asn_summary: "Goods in",
	approve_asn_delivery_slot_request: "Goods in",
	reject_asn_delivery_slot_request: "Goods in",
	view_extra_asn_details: "Goods in",
	force_asn_sync: "Goods in",
	view_returns_summary: "Returns",
	create_return_label: "Returns",
	force_return_sync: "Returns",
	view_order_claims: "Claims",
	create_order_claim: "Claims",
	raise_claim: "Claims",
	view_invoices: "Invoices",
	view_invoices_internal: "Invoices",
	create_invoice: "Invoices",
	upload_invoice: "Invoices",
	view_reports: "Reports",
	view_audit_logs: "Audit",
	edit_courier_configuration: "Courier",
	create_user_seller_role: "Users and roles",
	manage_seller_roles: "Users and roles",
	access_all_sellers: "Staff",
	access_frozen_seller: "Staff",
	view_debug_page: "Staff",
};

export function permissionDescription(code) {
	return PERMISSION_DESCRIPTIONS[code] || code;
}

export function permissionCategory(code) {
	return PERMISSION_CATEGORIES[code] || OTHER_PERMISSION_CATEGORY;
}

function comparePermissionsByDescription(a, b) {
	return permissionDescription(a).localeCompare(permissionDescription(b));
}

/**
 * Group permission codes by catalogue category (stable order).
 * Empty categories are omitted. Unknown codes go in Other.
 */
export function groupPermissionsByCategory(codes) {
	const buckets = new Map();
	PERMISSION_CATEGORY_ORDER.forEach((category) => {
		buckets.set(category, []);
	});
	(codes || []).forEach((code) => {
		const category = permissionCategory(code);
		if (!buckets.has(category)) {
			buckets.set(category, []);
		}
		buckets.get(category).push(code);
	});
	return PERMISSION_CATEGORY_ORDER
		.filter((category) => buckets.has(category) && buckets.get(category).length > 0)
		.map((category) => ({
			category,
			permissions: [...buckets.get(category)].sort(comparePermissionsByDescription),
		}));
}

/** Permissions the caller may grant on a custom role (seller ⊆ own; elevated = catalogue). */
export function grantablePermissions(userdata) {
	const perms = userdata?.permissions;
	if (!perms) {
		return [];
	}
	if (perms.has(ACCESS_ALL_SELLERS_PERMISSION)) {
		return [...ALL_PERMISSION_CODES];
	}
	return Array.from(perms.keys()).sort();
}

/**
 * Split a role's permissions into ones the editor can toggle vs ones they cannot grant.
 * Edit save must only send `editable` (API rejects codes outside what the caller may grant).
 */
export function partitionRolePermissions(rolePermissions, grantable) {
	const grantableSet = new Set(grantable || []);
	const editable = [];
	const locked = [];
	(rolePermissions || []).forEach((code) => {
		if (grantableSet.has(code)) {
			editable.push(code);
		} else {
			locked.push(code);
		}
	});
	editable.sort();
	locked.sort();
	return { editable, locked };
}
